
import { useCallback, useEffect, useRef } from 'react';
import { useSessionInitialization } from './hooks/useSessionInitialization';
import { useProfileLoading } from './hooks/useProfileLoading';
import { useSessionRefresher } from './hooks/useSessionRefresher';
import { createLogger } from '@/services/utils/loggingUtils';

const logger = createLogger('useAuthSession');

export const useAuthSession = () => {
  // Session initialization hook (must come first to get initial session)
  const { user, setUser, session, setSession, isLoading, setIsLoading } = useSessionInitialization();
  
  // Session refresher hook
  const { refreshSession, refreshErrors, session: refreshedSession } = useSessionRefresher(session);
  
  // Track previous session to prevent unnecessary updates
  const prevSessionRef = useRef(session);
  
  // Update session when refresher has a new one
  useEffect(() => {
    if (refreshedSession && refreshedSession !== session && 
        JSON.stringify(refreshedSession) !== JSON.stringify(prevSessionRef.current)) {
      logger.debug('Session updated from refresher'); // Changed from info to debug
      prevSessionRef.current = refreshedSession;
      setSession(refreshedSession);
    }
  }, [refreshedSession, session, setSession]);
  
  // Profile loading hook
  const { profile, setProfile, loadUserProfile, resetProfileState } = useProfileLoading(refreshSession);
  
  // Track if profile is already loading to prevent duplicate requests
  const isLoadingProfileRef = useRef(false);
  
  // Load profile when user changes
  useEffect(() => {
    if (user && !profile && !isLoadingProfileRef.current) {
      isLoadingProfileRef.current = true;
      loadUserProfile(user.id).finally(() => {
        isLoadingProfileRef.current = false;
      });
    }
  }, [user, profile, loadUserProfile]);
  
  // Session management reset on signout
  useEffect(() => {
    if (!user && !isLoading) {
      resetProfileState();
    }
  }, [user, isLoading, resetProfileState]);

  // Combined refresh method
  const combinedRefreshSession = useCallback(async (): Promise<boolean> => {
    const success = await refreshSession();
    if (success && user && !profile) {
      await loadUserProfile(user.id);
    }
    return success;
  }, [refreshSession, user, profile, loadUserProfile]);

  return {
    user,
    session,
    profile,
    isLoading,
    refreshSession: combinedRefreshSession,
    refreshErrors,
    setProfile,
    setIsLoading
  };
};
